import { Box, Button, Checkbox, Popover, Stack, Typography } from '@mui/material';
import {
  BUSINESS_LIST_OPTIONS,
  CATEGORY_LIST_OPTIONS,
  COST_DASHBOARD_VARIANT,
  COST_SUMMARY_KINDS_OPTIONS,
  COST_SUMMARY_TYPES_OPTIONS,
  SUB_RESOURCE_LIST_OPTIONS,
} from 'pages/admin/CostDashboard/constants';
import { useEffect, useMemo, useState } from 'react';
import { BusinessCostRecord } from 'shared/types/protoc-gen/bffbillsystem';
import { ColumnConfig } from 'shared/types/table';

import { ResourceRankingFilter } from './metrics/ResourceRankingTable';

interface CostFilterPopoverProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  allValues: string[];
  selectedValues: string[];
  onChange: (selected: string[]) => void;
  onSort?: (direction: 'asc' | 'desc') => void;
  setResourceRankingFilter: React.Dispatch<React.SetStateAction<ResourceRankingFilter>>;
  column: ColumnConfig<BusinessCostRecord>;
  variant: COST_DASHBOARD_VARIANT;
}

const CostFilterPopover: React.FC<CostFilterPopoverProps> = ({
  allValues,
  anchorEl,
  column,
  onChange,
  onClose,
  open,
  selectedValues,
  variant,
}) => {
  const optionsMap: Record<string, { label: string; value: string }[]> = {
    kind: COST_SUMMARY_KINDS_OPTIONS.filter(opt => opt.value !== 'ALL'),
    type: COST_SUMMARY_TYPES_OPTIONS.filter(opt => opt.value !== 'ALL'),
    subResource: SUB_RESOURCE_LIST_OPTIONS.filter(opt => opt.value !== 'ALL'),
    business:
      variant === COST_DASHBOARD_VARIANT.ROLLUP ? BUSINESS_LIST_OPTIONS : CATEGORY_LIST_OPTIONS,
  };

  const [tempSelected, setTempSelected] = useState<string[]>([]);

  useEffect(() => {
    if (selectedValues.length === 0 && allValues.length > 0) {
      setTempSelected(allValues);
    } else {
      setTempSelected(selectedValues);
    }
  }, [selectedValues, allValues]);

  const columnOptions = optionsMap[column.id] || allValues.map(v => ({ label: v, value: v }));

  const filteredOptions = useMemo(() => {
    return columnOptions.filter(opt => opt.label.toLowerCase());
  }, [columnOptions]);

  const toggleValue = (val: string) => {
    setTempSelected(prev => (prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val]));
  };

  const handleApply = () => {
    onChange(tempSelected);
    onClose();
  };

  const handleClear = () => {
    setTempSelected([]);
  };

  return (
    <Popover
      anchorEl={anchorEl}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      onClose={onClose}
      open={open}
    >
      <Box sx={{ p: 2, width: 240 }}>
        <Typography fontWeight="bold" mb={1}>
          Filter by {column.Header}
        </Typography>

        <Stack spacing={1}>
          <Stack spacing={0.5} sx={{ maxHeight: 200, overflowY: 'auto' }}>
            {filteredOptions.map(({ label, value }) => (
              <Stack alignItems="center" direction="row" key={value} spacing={1}>
                <Checkbox
                  checked={tempSelected.includes(value)}
                  onChange={() => toggleValue(value)}
                />
                <Typography>{label}</Typography>
              </Stack>
            ))}
          </Stack>

          <Stack direction="row" justifyContent="space-between" mt={1}>
            <Button onClick={handleClear} size="small">
              Clear
            </Button>
            <Stack direction="row" spacing={1}>
              <Button onClick={onClose} size="small">
                Cancel
              </Button>
              <Button onClick={handleApply} size="small" variant="contained">
                OK
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </Box>
    </Popover>
  );
};

export default CostFilterPopover;
